import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import {
  Grid,
  Box,
  Paper,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Button,
  Typography,
  Chip,
  alpha,
  Avatar,
  Divider,
  IconButton,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { useParams, useHistory } from "react-router-dom";
import AlbumIcon from '@mui/icons-material/Album';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LinkIcon from '@mui/icons-material/Link';
import * as actions from "./actions";
import ViewLinksModal from "./ViewLinksModal";
import { API_URL } from "../../constants";
import moment from "moment";
import CustomShowModal from "../Common/customShowModal";
import ConfirmModal from "../Common/ConfirmModal";
import CustomAddModal from "../Common/CustomAddModal";
import { toastSuccess } from "../../utils";

// Styled Components
const GlassCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  background: alpha('#1E293B', 0.5),
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha('#fff', 0.08)}`,
  boxShadow: `0 8px 32px ${alpha('#000', 0.3)}`,
  marginBottom: theme.spacing(3),
}));

const PageHeader = styled(Box)(({ theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: '#F1F5F9',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#94A3B8',
  fontSize: '0.875rem',
  minWidth: '180px',
  flexShrink: 0,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: '#E2E8F0',
  fontSize: '0.875rem',
  fontWeight: 500,
  flex: 1,
  wordBreak: 'break-word',
}));

const AlbumArtworkContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: alpha('#0F172A', 0.5),
  border: `1px solid ${alpha('#fff', 0.08)}`,
  aspectRatio: '1/1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const StatusBadge = styled(Chip)(({ status }) => {
  const colors = {
    approved: { bg: alpha('#66BB6A', 0.15), color: '#66BB6A', border: alpha('#66BB6A', 0.3) },
    rejected: { bg: alpha('#FF6B6B', 0.15), color: '#FF6B6B', border: alpha('#FF6B6B', 0.3) },
    in_review: { bg: alpha('#FFA726', 0.15), color: '#FFA726', border: alpha('#FFA726', 0.3) },
    takendown: { bg: alpha('#FF6B6B', 0.15), color: '#FF6B6B', border: alpha('#FF6B6B', 0.3) },
  };
  const statusColor = colors[status] || colors.in_review;
  return {
    backgroundColor: statusColor.bg,
    color: statusColor.color,
    border: `1px solid ${statusColor.border}`,
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 32,
  };
});

const getStatusLabel = (status) => {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'takendown':
      return 'Takendown';
    case 'in_review':
      return 'In Review';
    default:
      return 'Unknown';
  }
};

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      backgroundColor: alpha('#12B4A1', 0.1),
      color: '#12B4A1',
      fontWeight: 700,
      fontSize: '0.875rem',
      borderBottom: `2px solid ${alpha('#12B4A1', 0.3)}`,
      padding: '16px',
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      transition: 'background-color 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: alpha('#12B4A1', 0.05),
      },
    },
    '& .MuiTableCell-root': {
      color: '#E2E8F0',
      borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
      padding: '16px',
    },
  },
}));

const UserInfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: alpha('#12B4A1', 0.05),
  borderRadius: 12,
  border: `1px solid ${alpha('#12B4A1', 0.1)}`,
}));

function ViewReleases(props) {
  const params = useParams();
  const history = useHistory();

  const [release, setRelease] = useState({});
  const [openSong, setOpenSong] = useState(false);
  const [selectedSong, setSelectedSong] = useState({});
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedId] = useState(null);
  const [openReject, setOpenReject] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [showSongModal, setShowSongModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [openLinksModal, setOpenLinksModal] = useState(false);
  const [selectedAudioForLinks, setSelectedAudioForLinks] = useState(null);
  const [logos, setLogos] = useState([]);

  const onGetRelease = React.useCallback(() => {
    props.setLoader(true);
    actions
      .getNewReleaseByIdLabel(params.id)
      .then((res) => {
        props.setLoader(false);
        setRelease(res.body);
        setImageError(false);
      })
      .catch((err) => {
        props.setLoader(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    onGetRelease();
    actions.getLogos().then((res) => {
      setLogos(
        (res.body || []).filter((item) => item.type === '1')
      );
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const onOpenModal = (item) => {
    setSelectedSong(item);
    setOpenSong(true);
  };

  const onConfirmAcceptModal = () => {
    props.setLoader(true);
    actions
      .changeReleaseStatusAdmin({ status: "approved", release_id: selectedId })
      .then((res) => {
        setOpenConfirm(false);
        props.setLoader(false);
        onGetRelease();
      })
      .catch((err) => {
        props.setLoader(false);
      });
  };

  const onSubmitReject = () => {
    props.setLoader(true);
    actions
      .changeReleaseStatusAdmin({
        release_id: selectedId,
        status: "rejected",
        reason: reasonText,
      })
      .then((res) => {
        setOpenReject(false);
        props.setLoader(false);
        onGetRelease();
        toastSuccess(res.message);
      })
      .catch((err) => {
        props.setLoader(false);
      });
  };

  const onClickAccept = (id) => {
    console.log("onClickAccept triggered");
  };

  const onClickReject = (id) => {
    console.log("onClickReject triggered");
  };

  const {
    album_artwork = "",
    audios = [],
    featuring1 = "",
    featuring2 = "",
    featuring3 = "",
    genre = "",
    lable_name = "",
    number_of_tracks = "",
    primary_artist1 = "",
    primary_artist2 = "",
    primary_artist3 = "",
    release_date = "",
    release_id = "",
    release_title = "",
    release_type = "",
    status = "",
    sub_genre = "",
    name = "",
    email = "",
  } = release;

  const imageUrl = album_artwork ? `${API_URL}/${album_artwork}` : '';

  return (
    <Box>
      <Helmet>
        <title>{release.release_title ? `${release.release_title} - Fronic Media Labels` : 'View Release - Fronic Media Labels'}</title>
      </Helmet>

      {/* Header with Back Button */}
      <PageHeader>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => history.goBack()}
            sx={{
              backgroundColor: alpha('#12B4A1', 0.1),
              color: '#12B4A1',
              '&:hover': { backgroundColor: alpha('#12B4A1', 0.2) },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#F1F5F9', mb: 0.5 }}>
              {release_title || 'Release Details'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
              Release ID: {release_id || '-'}
            </Typography>
          </Box>
        </Box>
        <StatusBadge label={getStatusLabel(status)} status={status} size="medium" />
      </PageHeader>

      <Grid container spacing={3}>
        {/* Left Column - Album Artwork & User Info */}
        <Grid item xs={12} md={4}>
          <GlassCard>
            <AlbumArtworkContainer>
              {imageError || !imageUrl ? (
                <Box textAlign="center">
                  <ImageIcon sx={{ fontSize: 80, color: alpha('#94A3B8', 0.3), mb: 2 }} />
                  <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                    Artwork not available
                  </Typography>
                </Box>
              ) : (
                <img
                  src={imageUrl}
                  alt={release_title}
                  onError={() => setImageError(true)}
                />
              )}
            </AlbumArtworkContainer>

            <Box mt={3}>
              <SectionTitle>
                <AlbumIcon fontSize="small" />
                User Information
              </SectionTitle>
              <UserInfoBox>
                <Avatar sx={{ bgcolor: alpha('#12B4A1', 0.2), color: '#12B4A1' }}>
                  {name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box flex={1}>
                  <Typography sx={{ fontWeight: 600, color: '#F1F5F9', fontSize: '0.95rem' }}>
                    {name || 'Unknown'}
                  </Typography>
                  <Typography sx={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                    {email || '-'}
                  </Typography>
                </Box>
              </UserInfoBox>
            </Box>

          </GlassCard>
        </Grid>

        {/* Right Column - Release Information */}
        <Grid item xs={12} md={8}>
          {/* Basic Information */}
          <GlassCard>
            <SectionTitle>Basic Information</SectionTitle>
            <Box>
              <InfoRow>
                <InfoLabel>Release Type</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{release_type || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Label</InfoLabel>
                <InfoValue>{lable_name || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Genre</InfoLabel>
                <InfoValue>{genre || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Sub Genre</InfoLabel>
                <InfoValue>{sub_genre || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Number of Tracks</InfoLabel>
                <InfoValue>{number_of_tracks || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Release Date</InfoLabel>
                <InfoValue>{release_date ? moment(Number(release_date)).format("MMM DD, YYYY") : '-'}</InfoValue>
              </InfoRow>
            </Box>
          </GlassCard>

          {/* Primary Artists */}
          {primary_artist1 && (
            <GlassCard>
              <SectionTitle>Primary Artists</SectionTitle>
              <Box>
                <InfoRow>
                  <InfoLabel>Primary Artist 1</InfoLabel>
                  <InfoValue>{primary_artist1}</InfoValue>
                </InfoRow>

                {primary_artist2 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.06) }} />
                    <InfoRow>
                      <InfoLabel>Primary Artist 2</InfoLabel>
                      <InfoValue>{primary_artist2}</InfoValue>
                    </InfoRow>
                  </>
                )}

                {primary_artist3 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.06) }} />
                    <InfoRow>
                      <InfoLabel>Primary Artist 3</InfoLabel>
                      <InfoValue>{primary_artist3}</InfoValue>
                    </InfoRow>
                  </>
                )}
              </Box>
            </GlassCard>
          )}

          {/* Featuring Artists */}
          {featuring1 && (
            <GlassCard>
              <SectionTitle>Featuring Artists</SectionTitle>
              <Box>
                <InfoRow>
                  <InfoLabel>Featuring Artist 1</InfoLabel>
                  <InfoValue>{featuring1}</InfoValue>
                </InfoRow>

                {featuring2 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.06) }} />
                    <InfoRow>
                      <InfoLabel>Featuring Artist 2</InfoLabel>
                      <InfoValue>{featuring2}</InfoValue>
                    </InfoRow>
                  </>
                )}

                {featuring3 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.06) }} />
                    <InfoRow>
                      <InfoLabel>Featuring Artist 3</InfoLabel>
                      <InfoValue>{featuring3}</InfoValue>
                    </InfoRow>
                  </>
                )}
              </Box>
            </GlassCard>
          )}
        </Grid>
      </Grid>

      {/* Tracks Table */}
      {audios && audios.length > 0 && (
        <GlassCard>
          <SectionTitle sx={{ mb: 3 }}>
            <PlayArrowIcon fontSize="small" />
            Tracks ({audios.length})
          </SectionTitle>
          <Box sx={{ overflowX: 'auto' }}>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Title</TableCell>
                  <TableCell>Artist</TableCell>
                  <TableCell>Genre</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Explicit</TableCell>
                  <TableCell>Links</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audios.map((audio, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, color: '#F1F5F9' }}>
                        {audio.title || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#E2E8F0', fontSize: '0.875rem' }}>
                        {[audio.primary_artist1, audio.primary_artist2, audio.primary_artist3].filter(Boolean).join(', ') || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{audio.genre || '-'}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{audio.track_title_language || '-'}</TableCell>
                    <TableCell>
                      {audio.explicit_version?.toLowerCase() === 'yes' ? (
                        <Chip label="Yes" size="small" color="error" sx={{ fontSize: '0.75rem' }} />
                      ) : (
                        <Chip label="No" size="small" sx={{ fontSize: '0.75rem', bgcolor: alpha('#66BB6A', 0.15), color: '#66BB6A' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<LinkIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAudioForLinks(audio);
                          setOpenLinksModal(true);
                        }}
                        sx={{
                          background: 'linear-gradient(135deg, #139979 0%, #12B4A1 50%, #0596B3 100%)',
                          color: '#fff',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: '8px',
                          px: 1.5,
                          py: 0.5,
                          minWidth: 0,
                          boxShadow: '0 2px 8px rgba(18,180,161,0.3)',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0e7a61 0%, #0f9d8b 50%, #047a93 100%)',
                            boxShadow: '0 4px 12px rgba(18,180,161,0.4)',
                          },
                        }}
                      >
                        Fetch Links
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </Box>
        </GlassCard>
      )}

      {/* Modals */}
      <CustomShowModal
        open={openSong}
        handleClose={() => setOpenSong(false)}
        data={selectedSong}
      />

      <ConfirmModal
        open={openConfirm}
        title="Confirm Approval"
        message="Are you sure you want to approve this release?"
        onConfirm={onConfirmAcceptModal}
        onClose={() => setOpenConfirm(false)}
      />

      <ViewLinksModal
        open={openLinksModal}
        handleClose={() => { setOpenLinksModal(false); setSelectedAudioForLinks(null); }}
        audio={selectedAudioForLinks}
        logos={logos}
      />

      <CustomAddModal
        open={openReject}
        handleClose={() => setOpenReject(false)}
        title="Reject Release"
      >
        <Box p={2}>
          <Typography sx={{ color: '#E2E8F0', mb: 2 }}>
            Enter reason for rejection:
          </Typography>
          <textarea
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder="Enter rejection reason..."
            style={{
              width: '100%',
              minHeight: 100,
              padding: 12,
              backgroundColor: alpha('#0F172A', 0.5),
              border: `1px solid ${alpha('#fff', 0.1)}`,
              borderRadius: 8,
              color: '#E2E8F0',
              fontSize: '0.875rem',
              resize: 'vertical',
            }}
          />
          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={() => setOpenReject(false)}
              fullWidth
              sx={{
                borderColor: alpha('#fff', 0.2),
                color: '#E2E8F0',
                '&:hover': { borderColor: alpha('#fff', 0.3), backgroundColor: alpha('#fff', 0.05) },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={onSubmitReject}
              disabled={!reasonText}
              fullWidth
            >
              Reject Release
            </Button>
          </Box>
        </Box>
      </CustomAddModal>

    </Box>
  );
}

export default ViewReleases;
